/**
 * Idle timer for session management.
 * Tracks user activity and triggers warning/logout on inactivity.
 */

import { getState } from '../state/store.js';
import { clearSession, setTimeoutWarning } from '../state/session.js';

// Timer configuration (in milliseconds)
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;      // 15 minutes total
const WARNING_BEFORE_MS = 2 * 60 * 1000;     // Show warning 2 minutes before

// Timer state
let idleTimer = null;
let warningTimer = null;
let countdownInterval = null;
let lastActivity = Date.now();
let isRunning = false;

// Countdown callback for UI updates
let countdownCallback = null;

/**
 * Set the countdown callback for UI updates.
 * @param {Function} callback - Called with seconds remaining
 */
export function setCountdownCallback(callback) {
  countdownCallback = callback;
}

/**
 * Reset the idle timer on user activity.
 */
function resetIdleTimer() {
  if (!isRunning) return;

  // Only reset if we're authenticated
  const { session } = getState();
  if (!session.authenticated) return;

  lastActivity = Date.now();

  // Clear existing timers
  clearTimeout(warningTimer);
  clearTimeout(idleTimer);
  clearInterval(countdownInterval);

  // Hide warning if showing
  setTimeoutWarning(false);

  // Set new timers
  warningTimer = setTimeout(showWarning, IDLE_TIMEOUT_MS - WARNING_BEFORE_MS);
  idleTimer = setTimeout(performLogout, IDLE_TIMEOUT_MS);
}

/**
 * Show the timeout warning and start countdown.
 */
function showWarning() {
  setTimeoutWarning(true);

  // Start countdown
  let secondsRemaining = Math.floor(WARNING_BEFORE_MS / 1000);

  // Initial callback
  if (countdownCallback) {
    countdownCallback(secondsRemaining);
  }

  countdownInterval = setInterval(() => {
    secondsRemaining--;

    if (countdownCallback) {
      countdownCallback(secondsRemaining);
    }

    if (secondsRemaining <= 0) {
      clearInterval(countdownInterval);
    }
  }, 1000);
}

/**
 * Perform automatic logout.
 */
function performLogout() {
  stopIdleTimer();
  setTimeoutWarning(false);
  clearSession();
}

/**
 * Start the idle timer.
 * Should be called when user authenticates.
 */
export function startIdleTimer() {
  if (isRunning) return;

  isRunning = true;
  lastActivity = Date.now();

  // Attach activity listeners
  const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
  activityEvents.forEach(event => {
    document.addEventListener(event, resetIdleTimer, { passive: true });
  });

  // Set initial timers
  warningTimer = setTimeout(showWarning, IDLE_TIMEOUT_MS - WARNING_BEFORE_MS);
  idleTimer = setTimeout(performLogout, IDLE_TIMEOUT_MS);

  console.log('Idle timer started');
}

/**
 * Stop the idle timer.
 * Should be called on logout or when needed.
 */
export function stopIdleTimer() {
  if (!isRunning) return;

  isRunning = false;

  // Clear all timers
  clearTimeout(warningTimer);
  clearTimeout(idleTimer);
  clearInterval(countdownInterval);
  warningTimer = null;
  idleTimer = null;
  countdownInterval = null;

  // Remove activity listeners
  const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
  activityEvents.forEach(event => {
    document.removeEventListener(event, resetIdleTimer);
  });

  console.log('Idle timer stopped');
}

/**
 * Extend the session (user clicked "Stay Logged In").
 * Resets all timers and hides warning.
 */
export function extendSession() {
  resetIdleTimer();
}

/**
 * Get time remaining until session expires.
 * @returns {number} Milliseconds remaining
 */
export function getTimeRemaining() {
  if (!isRunning) return 0;
  const elapsed = Date.now() - lastActivity;
  return Math.max(0, IDLE_TIMEOUT_MS - elapsed);
}

/**
 * Check if idle timer is currently running.
 * @returns {boolean} Whether timer is active
 */
export function isTimerRunning() {
  return isRunning;
}
